// Térképes helyválasztó a Helyszín-szerkesztőhöz: kattintással vagy a jelölő húzásával állítható
// a pont. Ugyanaz a térkép, mint az appban (OpenFreeMap „liberty” stílus + MapLibre GL).
// A MapLibre-t futás közben töltjük be (unpkg), így az admin buildjébe nem kell új csomag.
import React, { useEffect, useRef, useState } from 'react';

const ML_VER = '5.24.0';
const ML_JS = `https://unpkg.com/maplibre-gl@${ML_VER}/dist/maplibre-gl.js`;
const ML_CSS = `https://unpkg.com/maplibre-gl@${ML_VER}/dist/maplibre-gl.css`;
const STYLE = 'https://tiles.openfreemap.org/styles/liberty';
const BUDAPEST: [number, number] = [19.0402, 47.4979]; // [lng, lat]

/* eslint-disable @typescript-eslint/no-explicit-any */
let loading: Promise<any> | null = null;
function loadMapLibre(): Promise<any> {
  const w = window as any;
  if (w.maplibregl) return Promise.resolve(w.maplibregl);
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${ML_CSS}"]`)) {
      const l = document.createElement('link');
      l.rel = 'stylesheet'; l.href = ML_CSS;
      document.head.appendChild(l);
    }
    const s = document.createElement('script');
    s.src = ML_JS; s.async = true;
    s.onload = () => resolve(w.maplibregl);
    s.onerror = () => { loading = null; reject(new Error('A térkép nem tölthető be.')); };
    document.head.appendChild(s);
  });
  return loading;
}

export function MapPicker({ lat, lng, onPick, height = 260 }: {
  lat: number | null; lng: number | null; onPick: (lat: number, lng: number) => void; height?: number;
}) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const marker = useRef<any>(null);
  const pickRef = useRef(onPick);
  pickRef.current = onPick;
  const [err, setErr] = useState('');

  // térkép létrehozása egyszer
  useEffect(() => {
    let dead = false;
    loadMapLibre().then((ml) => {
      if (dead || !box.current) return;
      const has = lat !== null && lng !== null;
      const m = new ml.Map({
        container: box.current, style: STYLE,
        center: has ? [lng, lat] : BUDAPEST, zoom: has ? 16 : 11, attributionControl: { compact: true },
      });
      m.addControl(new ml.NavigationControl({ showCompass: false }), 'top-right');
      const mk = new ml.Marker({ color: '#00AFF2', draggable: true });
      if (has) mk.setLngLat([lng, lat]).addTo(m);
      mk.on('dragend', () => { const p = mk.getLngLat(); pickRef.current(round(p.lat), round(p.lng)); });
      m.on('click', (e: any) => {
        mk.setLngLat(e.lngLat).addTo(m);
        pickRef.current(round(e.lngLat.lat), round(e.lngLat.lng));
      });
      map.current = m; marker.current = mk;
    }).catch((e) => setErr(e.message));
    return () => { dead = true; map.current?.remove(); map.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // külső változás (kézi beírás, „Cím alapján”) → jelölő és nézet követi
  useEffect(() => {
    const m = map.current; const mk = marker.current;
    if (!m || !mk || lat === null || lng === null) return;
    const cur = mk.getLngLat?.();
    if (cur && Math.abs(cur.lat - lat) < 1e-7 && Math.abs(cur.lng - lng) < 1e-7) return;
    mk.setLngLat([lng, lat]).addTo(m);
    m.easeTo({ center: [lng, lat], zoom: Math.max(m.getZoom(), 16) });
  }, [lat, lng]);

  return (
    <div className="relative rounded-xl overflow-hidden border border-[#31383D] bg-[#1d232a]" style={{ height }}>
      <div className="absolute inset-0"><div ref={box} className="w-full h-full" /></div>
      {err && <div className="absolute inset-0 flex items-center justify-center text-[13px] text-[#8a8f99] px-4 text-center">{err} Add meg a koordinátát kézzel.</div>}
    </div>
  );
}

const round = (x: number) => Math.round(x * 1e6) / 1e6;
